import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
import { Upload, Back } from '../../assets/icons';
import '../../assets/css/vendor/product-form.css';
import apiConfig from '../../config/api_config';
// import { image } from 'html2canvas/dist/types/css/types/image';

const NewProduct = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [categories, setCategories] = useState([]);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        price: '',
        discount_price: '',
        stock_quantity: '',
        sku: '',
        weight: '',
        dimensions: '',
        manufacturer: '',

        is_featured: false,
        is_active: true
    });
    
    const [images, setImages] = useState([]);
    const [primaryImageIndex, setPrimaryImageIndex] = useState(0);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await apiConfig.get('/product-categories/');
            
            if (response?.results) {
                setCategories(response.results);
            } else if (response?.data) {
                setCategories(Array.isArray(response.data) ? response.data : []);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            name: file.name
        }));
        setImages(prev => [...prev, ...newImages]);
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        if (primaryImageIndex === index) {
            setPrimaryImageIndex(0);
        } else if (primaryImageIndex > index) {
            setPrimaryImageIndex(prev => prev - 1);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (!formData.name.trim()) {
            setError('Product name is required');
            return;
        }
        if (!formData.category) {
            setError('Please select a category');
            return;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            setError('Please enter a valid price');
            return;
        }
        if (!formData.sku.trim()) {
            setError('SKU is required');
            return;
        }

        setIsSubmitting(true);

        try {
            // Create product
            const productData = {
                name: formData.name,
                description: formData.description,
                category: parseInt(formData.category),
                price: parseFloat(formData.price),
                discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
                stock_quantity: parseInt(formData.stock_quantity) || 0,
                sku: formData.sku,
                weight: formData.weight ? parseFloat(formData.weight) : null,
                dimensions: formData.dimensions,
                manufacturer: formData.manufacturer,
                is_featured: formData.is_featured,
                is_active: formData.is_active
            };

            const response = await apiConfig.post('/vendors/me/products/', productData);
            
            const product = response || response.data;
            // Upload images if any
            if (images.length > 0 && product?.id) {
                for (let i = 0; i < images.length; i++) {
                    const imageFormData = new FormData();
                    imageFormData.append('image', images[i].file);
                    imageFormData.append('is_primary', i === primaryImageIndex ? 'true' : 'false');
                    
                    await apiConfig.post(
                        `/vendors/me/products/${product.id}/upload_image/`,
                        imageFormData,
                        { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                }
            }

            setSuccess('Product created successfully!');
            setTimeout(() => navigate('/vendor/products'), 1500);
        } catch (err) {
            console.error('Failed to create product:', err);
            const errorData = err.response?.data;
            let errorMessage = 'Failed to create product. Please try again.';
            
            if (errorData?.errors && typeof errorData.errors === 'object') {
                const fieldErrors = Object.entries(errorData.errors);
                if (fieldErrors.length > 0) {
                    const [field, messages] = fieldErrors[0];
                    errorMessage = `${field}: ${Array.isArray(messages) ? messages[0] : messages}`;
                }
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            }
            
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="product-form-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <img src={Back} alt="Back" />
                    Back to Products
                </button>
                <h1>Add New Product</h1>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <span className="alert-message">{error}</span>
                    <button type="button" className="alert-close" onClick={() => setError(null)}>×</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    <span className="alert-icon">✓</span>
                    <span className="alert-message">{success}</span>
                    <button type="button" className="alert-close" onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-grid">
                    <div className="form-main">
                        <section className="form-section">
                            <h2>Basic Information</h2>
                            
                            <FormInput
                                label="Product Name"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                showLabel
                                placeholder="Enter product name"
                            />

                            <FormInput
                                label="Description"
                                type="textarea"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                showLabel
                                placeholder="Describe your product..."
                            />

                            <div className="input-row">
                                <FormInput
                                    label="Category"
                                    type="select"
                                    value={formData.category}
                                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="Select category"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </FormInput>

                                <FormInput
                                    label="SKU"
                                    value={formData.sku}
                                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="e.g., FF-001"
                                />
                            </div>
                        </section>

                        <section className="form-section">
                            <h2>Pricing & Stock</h2>
                            
                            <div className="input-row">
                                <FormInput
                                    label="Price (₦)"
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                />

                                <FormInput
                                    label="Discount Price (₦)"
                                    type="number"
                                    value={formData.discount_price}
                                    onChange={(e) => setFormData({...formData, discount_price: e.target.value})}
                                    showLabel
                                    placeholder="Optional"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <FormInput
                                label="Stock Quantity"
                                type="number"
                                value={formData.stock_quantity}
                                onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                                showLabel
                                placeholder="0"
                                min="0"
                            />
                        </section>

                        <section className="form-section">
                            <h2>Product Details</h2>
                            
                            <div className="input-row">
                                <FormInput
                                    label="Weight (kg)"
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                                    showLabel
                                    placeholder="Optional"
                                    min="0"
                                    step="0.01"
                                />

                                <FormInput
                                    label="Dimensions"
                                    value={formData.dimensions}
                                    onChange={(e) => setFormData({...formData, dimensions: e.target.value})}
                                    showLabel
                                    placeholder="e.g., 10x20x5 cm"
                                />
                            </div>

                            <FormInput
                                label="Manufacturer"
                                value={formData.manufacturer}
                                onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                                showLabel
                                placeholder="Brand or manufacturer name"
                            />
                        </section>
                    </div>

                    <div className="form-sidebar">
                        <section className="form-section">
                            <h2>Product Images</h2>
                            
                            <div className="image-upload-area">
                                {images.length > 0 ? (
                                    <div className="image-previews">
                                        {images.map((img, index) => (
                                            <div 
                                                key={index} 
                                                className={`image-preview ${index === primaryImageIndex ? 'primary' : ''}`}
                                                onClick={() => setPrimaryImageIndex(index)}
                                            >
                                                <img src={img.preview} alt={img.name} />
                                                <button 
                                                    type="button" 
                                                    className="remove-image"
                                                    onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                                                >
                                                    ×
                                                </button>
                                                {index === primaryImageIndex && (
                                                    <span className="primary-badge">Primary</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <img src={Upload} alt="Upload" />
                                        <p>Drop images here or click to upload</p>
                                    </div>
                                )}
                                
                                <div className="file-input-wrapper">
                                    <Button
                                        text={images.length > 0 ? 'Add More Images' : 'Upload Images'}
                                        variant="outline"
                                        icon={<img src={Upload} alt="Upload" />}
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        className="file-input"
                                    />
                                </div>
                                <small>Click an image to set as primary. PNG, JPG up to 5MB each.</small>
                            </div>
                        </section>

                        <section className="form-section">
                            <h2>Product Status</h2>
                            
                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                />
                                <span className="checkbox-label">Active (visible in store)</span>
                            </label>

                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.is_featured}
                                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                                />
                                <span className="checkbox-label">Featured product</span>
                            </label>
                        </section>

                        <div className="form-actions">
                            <Button
                                text="Cancel"
                                variant="outline"
                                onClick={() => navigate(-1)}
                            />
                            <Button
                                text={isSubmitting ? 'Creating...' : 'Create Product'}
                                type="submit"
                                variant="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default NewProduct;
